import { createContext, type Dispatch, type SetStateAction, useContext } from "react";
import { type BindableSlot, slot, useBindableValue } from "@package/react/bindable";
import type { IChartApi } from "lightweight-charts";

import type { ChartSettings, TCurveSettings } from "../../../data/live-charts/config";

export type TChartDataContext = {
  apiRef: BindableSlot<IChartApi>;
  chart?: ChartSettings;
  isEphemeral?: boolean;
  visibleWindow: number | null;
  setVisibleWindow: Dispatch<SetStateAction<number | null>>;
  reloading: boolean;
  setReloading: Dispatch<SetStateAction<boolean>>;
  loading: boolean;
  setLoading: Dispatch<SetStateAction<boolean>>;
  toggleLoad: () => void;
  curve_settings?: TCurveSettings;
  updateChart: (f: (settings: ChartSettings) => Partial<ChartSettings>) => void;
  updateCurveSettings: (f: (settings: TCurveSettings) => TCurveSettings) => void;
  scheduleResetTimescale?: () => void;
  backgroundLoading?: boolean;
  setBackgroundLoading?: Dispatch<SetStateAction<boolean>>;
  // True while a post-wake gap recovery is fetching/merging missed candles. Drives the covering overlay (hide the churn + block interaction) and tells auto-scroll to suspend its normal follow logic and snap to the latest once recovery completes.
  recovering?: boolean;
  setRecovering?: Dispatch<SetStateAction<boolean>>;
  remove?: () => void;
  toggleCursorEffects: (state: boolean) => void;
};

const ChartContext = createContext<TChartDataContext>({
  apiRef: slot(),
  chart: undefined,
  visibleWindow: null,
  setVisibleWindow: () => null,
  reloading: false,
  setReloading: () => null,
  loading: false,
  setLoading: () => null,
  toggleLoad: () => null,
  curve_settings: undefined,
  updateChart: () => void 0,
  updateCurveSettings: () => void 0,
  toggleCursorEffects: () => void 0,
});

export const ChartDataInnerProvider = ChartContext.Provider;

export const useChartDataContext = () => useContext(ChartContext);
export const useChartApi = () => useBindableValue(useChartDataContext().apiRef);
